import axios, { AxiosHeaders, CanceledError } from 'axios';
import { store } from '../../store';
import { refreshAccessToken, logout } from './authSlice';
import { isLoggedIn } from './authService';

const api = axios.create({ baseURL: window.env.API_URL });

api.interceptors.request.use(async config => {
  const access = store.getState().auth.access;
  if (access && !isLoggedIn(access)) {
    await store.dispatch(refreshAccessToken(undefined)).unwrap();
  }
  if (config.signal?.aborted) throw new CanceledError('Request canceled');
  const currentAccess = store.getState().auth.access;
  if (currentAccess) config.headers.set('Authorization', `Bearer ${currentAccess}`);
  return config;
});

api.interceptors.response.use(response => response, async error => {
  const request = error.config;
  if (error.response?.status !== 401 || !request || request.signal?.aborted) {
    return Promise.reject(error);
  }
  // Responses still in flight when the session ended must not start another refresh.
  if (!store.getState().auth.isAuthenticated) return Promise.reject(error);
  if (request._retry) {
    if (AxiosHeaders.from(request.headers).get('Authorization') === `Bearer ${store.getState().auth.access}`) {
      store.dispatch(logout());
    }
    return Promise.reject(error);
  }
  request._retry = true;
  const headers = AxiosHeaders.from(request.headers);
  const access = store.getState().auth.access;
  // A late 401 for an older token can use the replacement already obtained.
  if (!access || !headers.get('Authorization') || headers.get('Authorization') === `Bearer ${access}`) {
    try {
      await store.dispatch(refreshAccessToken(true)).unwrap();
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  }
  if (request.signal?.aborted) throw new CanceledError('Request canceled');
  const currentAccess = store.getState().auth.access;
  if (!currentAccess) return Promise.reject(error);
  headers.set('Authorization', `Bearer ${currentAccess}`);
  request.headers = headers;
  return api(request);
});

export default api;
